import { useEffect, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import {
  ArrowRightLeft,
  TextSearch,
  BarChart3,
  CalendarClock,
  ChevronDown,
  ChevronRight,
  ClipboardList,
  Columns3,
  FolderKanban,
  Home,
  Inbox,
  Building2,
  ClipboardCheck,
  FilePlus2,
  LayoutDashboard,
  LayoutGrid,
  ListChecks,
  LogOut,
  ShieldCheck,
  UserCircle,
  GripVertical,
  Monitor,
  Moon,
  Star,
  Sun,
  Trophy,
  Users,
  ServerCog,
  SlidersHorizontal,
  type LucideIcon,
  Bot,
  Mail,
  BookOpen,
} from "lucide-react";
import { useAuth } from "@/lib/auth";
import {
  THEMES,
  applyTheme,
  isTheme,
  readStoredTheme,
  storeTheme,
  watchSystemTheme,
  type Theme,
} from "@/lib/theme";
import { HealthIndicator } from "@/components/system/HealthIndicator";
import {
  DndContext,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  useDroppable,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import api from "@/lib/api";
import { toast } from "sonner";
import { cn } from "@/lib/cn";
import { atLeast } from "@/lib/roles";

interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  section: string;
  /** Sub-items shown nested under this item when its chevron is expanded.
   *  Parent stays clickable (navigates to its own route); the chevron is
   *  a separate clickable area that toggles expand. Children inherit the
   *  parent's section. Children should NOT have their own children
   *  (one level only). */
  children?: Array<Omit<NavItem, "section" | "children">>;
  /** Lowest role that sees this item (2026-09-29); default = everyone. */
  minRole?: "user" | "pmo" | "admin";
}

// Items render in array order, grouped by `section`. The MORE section
// (when expanded) preserves these section headers; PINNED items render
// as a flat list at the top, no header.
//
// Section order = order each section first appears in this array.
const NAV_ITEMS: NavItem[] = [
  // Strategy & Roadmap
  { to: "/executive", label: "Executive", icon: Home, section: "Strategy & Roadmap" },
  // The Monday Top-10 board email, previewed and sent from the planner
  // (Brett, 2026-09-21).
  { to: "/weekly-update", label: "Weekly Update", icon: Mail, section: "Strategy & Roadmap" },
  // The business's prioritization board — Top 10 / Just Do Its / Next up
  // / Unscoped / Paused (from the exec prioritization deck).
  { to: "/top-projects", label: "Top Projects", icon: Trophy, section: "Strategy & Roadmap" },
  // /review retired 2026-09-10 — Top Projects is the exec meeting page.
  { to: "/portfolio", label: "Portfolio", icon: FolderKanban, section: "Strategy & Roadmap" },
  { to: "/departments", label: "Departments", icon: Building2, section: "Strategy & Roadmap" },

  // Flow — work-tracking boards (the Jira-replacement app in the suite).
  // Open to every user; each board's own access (teams + invites)
  // decides what they see.
  { to: "/flow", label: "Boards", icon: Columns3, section: "Flow" },
  { to: "/flow/my-work", label: "My Work", icon: ListChecks, section: "Flow" },
  { to: "/flow/issues", label: "Issues", icon: TextSearch, section: "Flow" },

  // Dashboards — Freshservice operational views. Portfolio Health
  // and Dev Date Compliance RETIRED 2026-09-15: exec health lives on
  // the Top Projects board; the Jira date-fill report went unused.
  {
    to: "/service-desk",
    label: "Service Desk",
    icon: LayoutDashboard,
    section: "Dashboards",
  },
  {
    to: "/application-support/dashboard",
    label: "Application Support",
    icon: LayoutDashboard,
    section: "Dashboards",
  },
  // Synnergie support metrics (2026-09-29) — PMO/Admin only.
  { to: "/dashboards/synnergie-support", label: "Synnergie Support", icon: LayoutDashboard, section: "Dashboards", minRole: "pmo" },

  // Capacity & People
  { to: "/capacity", label: "Capacity", icon: BarChart3, section: "Capacity & People" },
  // /planning (scheduling bench + what-if) retired 2026-09-04 — the
  // Top Projects board owns priority order, and dates/staffing live on
  // project pages. The route redirects to /top-projects.
  { to: "/roles", label: "Role Overview", icon: LayoutGrid, section: "Capacity & People" },
  { to: "/roster", label: "Roster", icon: Users, section: "Capacity & People" },
  // All Teams is the combined-view parent: pools Synnergie + Systems
  // Applications + Business Analysts. It exists primarily for the
  // executive audience (CFO/VP) who care about the org-wide picture and
  // where the imbalance is, not about a single team in isolation.
  // Per-team pages live as children.
  {
    to: "/teams/all",
    label: "All Teams",
    icon: Users,
    section: "Capacity & People",
    children: [
      { to: "/teams/synnergie", label: "Synnergie", icon: Users },
      { to: "/teams/applications-development", label: "Systems Applications", icon: Users },
      { to: "/teams/business-analysts", label: "Business Analysts", icon: Users },
    ],
  },
  // Time RETIRED 2026-09-15 — the Jira worklog rollup went unused.

  // Intake & Process
  // Intake is the executive-level destination: "what needs my approval,
  // and what's in the pipeline." Lives FIRST — the answer most users
  // walk in looking for. /request is the public business-user wizard;
  // submissions land in /intake as Requested projects alongside FS imports.
  { to: "/request", label: "Request a Project", icon: FilePlus2, section: "Intake & Process" },
  { to: "/intake", label: "Intake", icon: ClipboardCheck, section: "Intake & Process" },
  // The Enhancements page retired 2026-10-01 — Small Enhancements is a
  // view on Top Projects (/top-projects?view=enhancements).
  // Application Support queue page retired 2026-09-10 (Brett: not
  // needed) — the route redirects to /triage. Its health dashboard
  // stays in the Dashboards section above.

  // System
  { to: "/settings", label: "Settings", icon: SlidersHorizontal, section: "System" },
  { to: "/system", label: "System", icon: ServerCog, section: "System" },
  { to: "/system/jira-migration", label: "Jira Migration", icon: ArrowRightLeft, section: "System" },
];

// ---------------------------------------------------------------------------
// Sidebar state — Option D refined: pinned items at the top + per-section
// accordion below. Each section header carries a count of unpinned items
// underneath, so the user knows what's there even when collapsed.
// All sections default to collapsed; the section containing the active
// page auto-expands so the user is never on a page that's hidden in nav.
// ---------------------------------------------------------------------------

const PINNED_STORAGE_KEY = "rp.sidebar.pinned";

// Droppable ids for the two zones: the pinned group (drop to pin /
// append) and the section accordion below it (drop to unpin).
const PIN_DROP_ID = "sidebar:pinned";
const UNPIN_DROP_ID = "sidebar:sections";
const SECTION_COLLAPSED_KEY = "rp.sidebar.sectionCollapsed";

const DEFAULT_PINNED: string[] = [
  "/executive",
  "/top-projects",
  "/portfolio",
  "/capacity",
];

/** Per-section explicit user choice. Absent key = use default (collapsed).
 *  Storage shape: { "Strategy & Roadmap": true, ... } where true = collapsed. */
type CollapsedMap = Record<string, boolean>;

/** Last-known order for instant first paint. The SERVER is the source
 *  of truth (users.sidebar_pinned) so an arrangement follows the user
 *  between machines; this mirror just avoids a flash of defaults while
 *  /auth/me is in flight. */
function loadPinnedMirror(): string[] {
  if (typeof window === "undefined") return DEFAULT_PINNED;
  try {
    const raw = window.localStorage.getItem(PINNED_STORAGE_KEY);
    if (raw === null) return DEFAULT_PINNED; // first ever load → seed defaults
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) return parsed.filter((x) => typeof x === "string");
  } catch {
    /* fall through */
  }
  return DEFAULT_PINNED;
}

function savePinnedMirror(pinned: string[]): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(PINNED_STORAGE_KEY, JSON.stringify(pinned));
  } catch {
    /* fall through */
  }
}

function loadCollapsed(): CollapsedMap {
  if (typeof window === "undefined") return {};
  try {
    const raw = window.localStorage.getItem(SECTION_COLLAPSED_KEY);
    if (raw === null) return {};
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === "object") return parsed as CollapsedMap;
  } catch {
    /* fall through */
  }
  return {};
}

function saveCollapsed(map: CollapsedMap): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(SECTION_COLLAPSED_KEY, JSON.stringify(map));
  } catch {
    /* fall through */
  }
}

// ---------------------------------------------------------------------------
// Item-level expand/collapse — for nav items that have children (e.g.
// Application Support → Dashboard). Distinct from section-level collapse.
// ---------------------------------------------------------------------------
const ITEM_EXPANDED_KEY = "rp.sidebar.itemExpanded";

/** Map of nav-item `to` → expanded state. Default for any item not in
 *  the map: collapsed (false). */
type ItemExpandedMap = Record<string, boolean>;

function loadItemExpanded(): ItemExpandedMap {
  if (typeof window === "undefined") return {};
  try {
    const raw = window.localStorage.getItem(ITEM_EXPANDED_KEY);
    if (raw === null) return {};
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === "object") return parsed as ItemExpandedMap;
  } catch {
    /* fall through */
  }
  return {};
}

function saveItemExpanded(map: ItemExpandedMap): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(ITEM_EXPANDED_KEY, JSON.stringify(map));
  } catch {
    /* fall through */
  }
}

export function Sidebar() {
  const [pinned, setPinned] = useState<string[]>(() => loadPinnedMirror());
  const [collapsed, setCollapsed] = useState<CollapsedMap>(() => loadCollapsed());
  const [itemExpanded, setItemExpanded] = useState<ItemExpandedMap>(() => loadItemExpanded());
  const location = useLocation();
  const { logout, user, updateUser } = useAuth();

  // Adopt the server's order once /auth/me lands (it's the source of
  // truth). null there means "never customized" — keep the defaults
  // this component already seeded.
  const serverPinned = user?.sidebar_pinned;
  useEffect(() => {
    if (!serverPinned) return;
    setPinned(serverPinned);
    savePinnedMirror(serverPinned);
  }, [serverPinned]);

  // Theme — same shape as the pins: the server's choice wins once
  // /auth/me lands; the localStorage mirror only exists so index.html can
  // paint the right theme before React mounts. Default Light.
  const [theme, setTheme] = useState<Theme>(() => readStoredTheme());
  const serverTheme = user?.theme;
  useEffect(() => {
    if (!isTheme(serverTheme)) return;
    setTheme(serverTheme);
    storeTheme(serverTheme);
  }, [serverTheme]);
  useEffect(() => {
    applyTheme(theme);
    return watchSystemTheme(theme);
  }, [theme]);
  const commitTheme = (next: Theme) => {
    setTheme(next);
    storeTheme(next);
    api
      .patch("/auth/me", { theme: next })
      .then(({ data }) => {
        if (user) updateUser({ ...user, theme: data.theme ?? next });
      })
      .catch(() => toast.error("Couldn't save your theme."));
  };

  // One writer for every pin mutation (pin, unpin, reorder): local state
  // first so the UI never lags the drag, then mirror + persist. A failed
  // save leaves the optimistic order on screen; the next load re-reads
  // the server, which is the honest thing to show.
  const commitPinned = (next: string[]) => {
    setPinned(next);
    savePinnedMirror(next);
    api
      .patch("/auth/me", { sidebar_pinned: next })
      .then(({ data }) => {
        if (user) updateUser({ ...user, sidebar_pinned: data.sidebar_pinned ?? next });
      })
      .catch(() => toast.error("Couldn't save your sidebar order."));
  };

  const sensors = useSensors(
    // 6px so a click-to-navigate never registers as a drag.
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor),
  );

  const pinnedSet = new Set(pinned);
  // Flow is admin-only while under review — hide its nav from regular users.
  const navItems = NAV_ITEMS.filter(
    (i) => {
      // Section visibility by role (2026-09-28): System is admin
      // territory; the dashboards need at least a User account. Flow is
      // open to everyone — board permissions gate it.
      if (i.section === "System") return atLeast(user?.role, "admin");
      if (i.minRole && !atLeast(user?.role, i.minRole)) return false;
      if (i.section === "Dashboards") return atLeast(user?.role, "user");
      return true;
    },
  );
  // Pinned items render in the user's pin order (so they can mentally
  // arrange them); unpinned items render in NAV_ITEMS order grouped
  // by section.
  const pinnedItems = pinned
    .map((to) => navItems.find((i) => i.to === to))
    .filter((i): i is NavItem => !!i);
  const unpinnedItems = navItems.filter((i) => !pinnedSet.has(i.to));

  // Group unpinned items by section. Section ordering follows their
  // first appearance in NAV_ITEMS (matches the order Brett confirmed).
  const grouped: Record<string, NavItem[]> = {};
  const sectionOrder: string[] = [];
  for (const item of unpinnedItems) {
    if (!grouped[item.section]) {
      grouped[item.section] = [];
      sectionOrder.push(item.section);
    }
    grouped[item.section].push(item);
  }

  // Section containing the active page — auto-expands so the active item
  // is always visible regardless of the user's collapse choice for that
  // section. We don't write this auto-expand to localStorage; the user's
  // explicit choice survives untouched.
  const activeItem = unpinnedItems.find((i) => i.to === location.pathname);
  const activeSection = activeItem?.section;

  // Default: collapsed. User explicit choice (false = open) wins.
  // Active section: always expanded regardless.
  const isExpanded = (section: string): boolean => {
    if (section === activeSection) return true;
    return collapsed[section] === false;
  };

  const togglePin = (to: string) => {
    commitPinned(
      pinned.includes(to) ? pinned.filter((p) => p !== to) : [...pinned, to],
    );
  };

  // Drop handling. Three cases:
  //   pinned row onto pinned row  → reorder
  //   unpinned row onto pinned    → pin, inserted at that position
  //   pinned row onto the section → unpin
  const onDragEnd = (e: DragEndEvent) => {
    const { active, over } = e;
    if (!over) return;
    const dragged = String(active.id);
    const target = String(over.id);
    if (dragged === target) return;

    if (target === UNPIN_DROP_ID) {
      if (pinnedSet.has(dragged)) commitPinned(pinned.filter((p) => p !== dragged));
      return;
    }

    const without = pinned.filter((p) => p !== dragged);
    const idx = without.indexOf(target);
    // Dropping on the pinned container (not a row) appends.
    const insertAt = target === PIN_DROP_ID || idx < 0 ? without.length : idx;
    const next = [...without.slice(0, insertAt), dragged, ...without.slice(insertAt)];
    commitPinned(next);
  };

  const toggleSection = (section: string) => {
    setCollapsed((prev) => {
      // Flip: explicit-open ↔ explicit-collapsed (no third "default" state
      // once the user has clicked — they own it from then on)
      const currentlyExpanded = prev[section] === false;
      const next = { ...prev, [section]: currentlyExpanded ? true : false };
      saveCollapsed(next);
      return next;
    });
  };

  const toggleItem = (to: string) => {
    setItemExpanded((prev) => {
      const next = { ...prev, [to]: !prev[to] };
      saveItemExpanded(next);
      return next;
    });
  };

  // Auto-expand a parent if the active page is one of its children — so
  // the user doesn't end up on a page that's hidden under a collapsed
  // chevron. Doesn't write to localStorage; user's explicit collapse
  // choice survives once they navigate elsewhere.
  const isItemExpanded = (item: NavItem): boolean => {
    if (itemExpanded[item.to]) return true;
    if (item.children?.some((c) => c.to === location.pathname)) return true;
    return false;
  };

  return (
    <aside className="flex w-56 flex-col border-r border-line bg-slate-900">
      {/* Brand — clicking returns to /executive, the de-facto home.
          Plain link so it works with browser back/forward and keeps the
          full URL clean. */}
      <NavLink
        to="/executive"
        className="flex h-14 items-center gap-2 border-b border-slate-700/50 px-5 transition-colors hover:bg-slate-800"
        title="Back to Executive (home)"
      >
        <BarChart3 className="h-5 w-5 text-indigo-400" />
        <span className="text-sm font-semibold tracking-tight text-white">
          Resource Planner
        </span>
      </NavLink>

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto px-3 py-4">
      <DndContext sensors={sensors} onDragEnd={onDragEnd}>
        {/* Pinned — flat list, no section header. Drag a row by its grip
            to reorder; drag one down into the accordion to unpin, or an
            accordion row up here to pin it at that spot. Order is saved
            per USER (users.sidebar_pinned), so it follows them. */}
        <PinnedZone hasItems={pinnedItems.length > 0}>
          <SortableContext
            items={pinnedItems.map((i) => i.to)}
            strategy={verticalListSortingStrategy}
          >
            {pinnedItems.map((item) => (
              <NavItemRow
                key={item.to}
                item={item}
                isPinned={true}
                draggable
                onTogglePin={togglePin}
                expanded={isItemExpanded(item)}
                onToggleExpanded={toggleItem}
              />
            ))}
          </SortableContext>
        </PinnedZone>

        {/* Divider between pinned and the per-section accordion — only
            when both have content */}
        {pinnedItems.length > 0 && sectionOrder.length > 0 && (
          <div className="my-3 border-t border-slate-800" />
        )}

        {/* Per-section accordion — each section header shows its count
            and a chevron; click to toggle. Default collapsed; section
            containing active page auto-expands. */}
        {sectionOrder.length > 0 && (
          <SectionsZone>
            {sectionOrder.map((section) => {
              const items = grouped[section];
              if (items.length === 0) return null; // hide empty sections
              const expanded = isExpanded(section);
              return (
                <div key={section}>
                  <button
                    onClick={() => toggleSection(section)}
                    className="flex w-full items-center gap-1.5 rounded-md px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-slate-500 transition-colors hover:bg-slate-800/60 hover:text-slate-300"
                    title={expanded ? `Hide ${section}` : `Show ${section}`}
                  >
                    {expanded ? (
                      <ChevronDown className="h-3 w-3" />
                    ) : (
                      <ChevronRight className="h-3 w-3" />
                    )}
                    <span className="flex-1 text-left">{section}</span>
                    <span className="rounded-full bg-slate-800 px-1.5 text-[9px] font-medium text-slate-400">
                      {items.length}
                    </span>
                  </button>
                  {expanded && (
                    <div className="mt-0.5 space-y-0.5">
                      {items.map((item) => (
                        <NavItemRow
                          key={item.to}
                          item={item}
                          isPinned={false}
                          draggable
                          onTogglePin={togglePin}
                          expanded={isItemExpanded(item)}
                          onToggleExpanded={toggleItem}
                        />
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </SectionsZone>
        )}
      </DndContext>
      </nav>

      {/* Footer */}
      <div className="space-y-0.5 border-t border-slate-700/50 px-3 py-3">
        <HealthIndicator variant="sidebar" />
        <NavLink
          to="/profile"
          className={({ isActive }) =>
            cn(
              "flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-sm font-medium transition-colors",
              isActive
                ? "bg-slate-800 text-slate-200"
                : "text-slate-500 hover:bg-slate-800 hover:text-slate-300",
            )
          }
        >
          <UserCircle className="h-4 w-4" />
          {user?.display_name || "Profile"}
        </NavLink>
        {user?.can_view_agents && (
          <NavLink
            to="/agents"
            className={({ isActive }) =>
              cn(
                "flex items-center gap-2.5 rounded-lg px-3 py-1.5 text-[13px] transition-colors",
                isActive
                  ? "bg-white/10 font-medium text-white"
                  : "text-slate-300 hover:bg-white/5 hover:text-white",
              )
            }
          >
            <Bot className="h-4 w-4 shrink-0" />
            Agents
          </NavLink>
        )}
        {/* Help & Training (2026-09-28): the Confluence user guide,
            mirrored into the planner so it works without a Confluence
            login. Edit link on the page itself for those who have one. */}
        <NavLink
          to="/help"
          data-testid="help-link"
          className={({ isActive }) =>
            cn(
              "flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-sm font-medium transition-colors",
              isActive
                ? "bg-slate-800 text-slate-200"
                : "text-slate-500 hover:bg-slate-800 hover:text-slate-300",
            )
          }
        >
          <BookOpen className="h-4 w-4" />
          Help &amp; Training
        </NavLink>
        {user?.role === "admin" && (
          <NavLink
            to="/admin"
            className={({ isActive }) =>
              cn(
                "flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-sm font-medium transition-colors",
                isActive
                  ? "bg-slate-800 text-slate-200"
                  : "text-slate-500 hover:bg-slate-800 hover:text-slate-300",
              )
            }
          >
            <ShieldCheck className="h-4 w-4" />
            Admin
          </NavLink>
        )}
        {/* Theme — Light / Dark / System. Segmented so the current choice is
            always visible; saved per user (users.theme) like the pin order. */}
        <div
          role="radiogroup"
          aria-label="Color theme"
          data-testid="theme-toggle"
          className="mx-1 my-1 grid grid-cols-3 gap-0.5 rounded-lg bg-slate-800/70 p-0.5"
        >
          {THEMES.map((t) => {
            const Icon = t === "light" ? Sun : t === "dark" ? Moon : Monitor;
            const label = t === "light" ? "Light" : t === "dark" ? "Dark" : "System";
            const active = theme === t;
            return (
              <button
                key={t}
                type="button"
                role="radio"
                aria-checked={active}
                aria-label={`Theme: ${label}`}
                title={`${label} theme`}
                onClick={() => commitTheme(t)}
                className={cn(
                  "flex items-center justify-center rounded-md p-1.5 transition-colors",
                  active
                    ? "bg-slate-700 text-slate-100 shadow-sm"
                    : "text-slate-500 hover:bg-slate-800 hover:text-slate-300",
                )}
              >
                <Icon className="h-3.5 w-3.5" />
              </button>
            );
          })}
        </div>
        <button
          onClick={logout}
          className="flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-sm font-medium text-slate-500 transition-colors hover:bg-slate-800 hover:text-slate-300"
        >
          <LogOut className="h-4 w-4" />
          Sign out
        </button>
        <p className="mt-2 px-3 text-[10px] text-slate-600">Resource Planner v{__APP_VERSION__}</p>
      </div>
    </aside>
  );
}

/** Drop zone for the pinned group. Also accepts a drop on empty space
 *  (appends), which is how the very first pin-by-drag works. */
function PinnedZone({
  hasItems,
  children,
}: {
  hasItems: boolean;
  children: React.ReactNode;
}) {
  const { setNodeRef, isOver } = useDroppable({ id: PIN_DROP_ID });
  return (
    <div
      ref={setNodeRef}
      className={cn(
        "space-y-0.5 rounded-lg transition-colors",
        isOver && "ring-1 ring-indigo-500/60",
        // Without items there's nothing to drop ON, so give the zone a
        // visible target that only appears mid-drag.
        !hasItems && isOver && "min-h-10 bg-slate-800/60",
      )}
    >
      {children}
      {!hasItems && isOver && (
        <p className="px-3 py-2 text-[11px] text-slate-400">Drop to pin to top</p>
      )}
    </div>
  );
}

/** Drop zone for the section accordion — dropping a pinned row here
 *  unpins it (it returns to its section). */
function SectionsZone({ children }: { children: React.ReactNode }) {
  const { setNodeRef, isOver } = useDroppable({ id: UNPIN_DROP_ID });
  return (
    <div
      ref={setNodeRef}
      className={cn(
        "space-y-1 rounded-lg transition-colors",
        isOver && "ring-1 ring-slate-600",
      )}
    >
      {children}
    </div>
  );
}

function NavItemRow({
  item,
  isPinned,
  draggable = false,
  onTogglePin,
  expanded = false,
  onToggleExpanded,
}: {
  item: NavItem;
  isPinned: boolean;
  /** Renders the grip and makes the row a drag source / drop target. */
  draggable?: boolean;
  onTogglePin: (to: string) => void;
  /** Only meaningful when `item.children` exists. */
  expanded?: boolean;
  onToggleExpanded?: (to: string) => void;
}) {
  const Icon = item.icon;
  const hasChildren = (item.children?.length ?? 0) > 0;
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: item.to, disabled: !draggable });
  return (
    <div
      ref={draggable ? setNodeRef : undefined}
      style={
        draggable
          ? { transform: CSS.Transform.toString(transform), transition }
          : undefined
      }
      className={cn(isDragging && "z-10 opacity-60")}
    >
      <NavLink
        to={item.to}
        end
        className={({ isActive }) =>
          cn(
            "group flex items-center gap-2.5 rounded-lg px-3 py-2 text-sm font-medium transition-colors",
            isActive
              ? "bg-indigo-600/20 text-indigo-300"
              : "text-slate-400 hover:bg-slate-800 hover:text-slate-200",
          )
        }
      >
        {/* Grip — the ONLY drag handle, so dragging can never be
            confused with clicking the link to navigate. Hover-revealed
            like the pin star, and it replaces nothing: the icon stays. */}
        {draggable && (
          <span
            {...attributes}
            {...listeners}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            title="Drag to reorder — up here to pin, down to unpin"
            aria-label={`Reorder ${item.label}`}
            className="-ml-1.5 cursor-grab text-slate-600 opacity-0 transition-opacity hover:text-slate-300 active:cursor-grabbing group-hover:opacity-100"
          >
            <GripVertical className="h-3 w-3" />
          </span>
        )}
        <Icon className="h-4 w-4 shrink-0" />
        <span className="flex-1 truncate">{item.label}</span>
        {/* Chevron — only when this item has children. Click toggles
            expand/collapse without navigating. preventDefault stops the
            NavLink's navigation; stopPropagation keeps the click local. */}
        {hasChildren && (
          <span
            role="button"
            tabIndex={0}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onToggleExpanded?.(item.to);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                e.stopPropagation();
                onToggleExpanded?.(item.to);
              }
            }}
            title={expanded ? "Hide sub-items" : "Show sub-items"}
            className="rounded p-0.5 text-slate-500 hover:bg-slate-700 hover:text-slate-300 transition-colors"
          >
            {expanded ? (
              <ChevronDown className="h-3 w-3" />
            ) : (
              <ChevronRight className="h-3 w-3" />
            )}
          </span>
        )}
        {/* Pin/unpin star — hover-only so it doesn't clutter. preventDefault
            on click stops the link navigation; stopPropagation keeps the
            click from bubbling to anything else. */}
        <span
          role="button"
          tabIndex={0}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onTogglePin(item.to);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              e.stopPropagation();
              onTogglePin(item.to);
            }
          }}
          title={isPinned ? "Unpin from top" : "Pin to top"}
          className={cn(
            "rounded p-0.5 transition-opacity",
            isPinned
              ? "text-amber-400 opacity-0 hover:bg-slate-700 group-hover:opacity-100"
              : "text-slate-500 opacity-0 hover:bg-slate-700 hover:text-amber-400 group-hover:opacity-100",
          )}
        >
          <Star className={cn("h-3 w-3", isPinned && "fill-current")} />
        </span>
      </NavLink>
      {/* Children — rendered indented when expanded. Smaller text, no
          pin star (children aren't independently pinnable; pin the
          parent to elevate the whole branch). */}
      {hasChildren && expanded && (
        <div className="mt-0.5 space-y-0.5 pl-6">
          {item.children!.map((child) => {
            const ChildIcon = child.icon;
            return (
              <NavLink
                key={child.to}
                to={child.to}
                end
                className={({ isActive }) =>
                  cn(
                    "flex items-center gap-2 rounded-md px-2.5 py-1.5 text-[12px] font-medium transition-colors",
                    isActive
                      ? "bg-indigo-600/20 text-indigo-300"
                      : "text-slate-500 hover:bg-slate-800 hover:text-slate-300",
                  )
                }
              >
                <ChildIcon className="h-3 w-3 shrink-0" />
                <span className="truncate">{child.label}</span>
              </NavLink>
            );
          })}
        </div>
      )}
    </div>
  );
}
